import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Select, MenuItem, FormControl,
    InputLabel, Switch, FormControlLabel, Box, Chip, Typography, CircularProgress
} from "@mui/material";
import { formatDate, addDays, differenceInDays, toLocalDay } from "../utils/dateUtils";
import styled from "@emotion/styled";
import MarkdownEditor from './MarkdownEditor';
import UserAssignmentDialog from './UserAssignmentDialog';
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { useSelector, useDispatch } from 'react-redux';
import { createTask, getSuggestedDueDate } from '../redux/ducks/task';
import { updateProject } from '../redux/slices/project';
import { getDepartments } from '../redux/ducks/department';
import { getTemplates } from '../redux/ducks/template';
import { showAlert } from '../redux/slices/alerts';
import FilePondUploader from "./FilePondUploader";
import { restoreAttachments } from "../utils/uploadUtils";
import { useIsLocked } from "../utils/lockUtils";
import { useHasPermission } from "../utils/permissionUtils";
import RetainerOverageNotice from "./RetainerOverageNotice";
import { useRetainerStatus } from "../hooks/useRetainerStatus";
import AppDatePicker from "./AppDatePicker";
import { selectableSkills, skillOptionLabel } from "../utils/skillUtils";
import TagPicker, { tagSourceNames, useTagDraft } from "./TagPicker";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ExtendProjectRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const Row = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
}));

const HalfWidthField = styled(FormField)(({ theme }) => ({
    width: '50%',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(4)
}));

const FieldHelper = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const UserChipsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const CreateTaskDialog = ({ open, onClose, projectId, accountId }) => {
    const dispatch = useDispatch();
    const skills = useSelector(state => state.skills);
    const [departments, setDepartments] = useState([]);
    const [checklistTemplates, setChecklistTemplates] = useState([]);
    const hasPermission = useHasPermission();
    const canViewTemplates = hasPermission('viewTemplates');
    const projectDepartment = useSelector(state => state.project.currentProject?.department);
    const projectDepartmentId = projectDepartment?._id || projectDepartment || '';
    const projectSkill = useSelector(state => state.project.currentProject?.skill);
    const projectSkillId = projectSkill?._id || projectSkill || '';
    const minStartDate = formatDate(useSelector(state => state.project.currentProject?.startDate));
    const projectDueDate = formatDate(useSelector(state => state.project.currentProject?.dueDate));
    const canEditProjects = hasPermission('editProjects');
    const projectIsOverdue = Boolean(projectDueDate) && projectDueDate < toLocalDay();
    const projectAccount = useSelector(state => state.project.currentProject?.account);
    const isLocked = useIsLocked(projectAccount, projectAccount?.client);
    const { status: retainerStatus } = useRetainerStatus({ accountId: accountId || projectAccount?._id, enabled: open });
    const [overageOverride, setOverageOverride] = useState(false);
    const isOverageBlocked = retainerStatus?.level === "block" && !overageOverride;
    const tagClientId = projectAccount?.client?._id || projectAccount?.client || null;
    const tagDraft = useTagDraft({ enabled: open, parentType: 'Project', parentId: projectId });
    const currentProject = useSelector(state => state.project.currentProject);
    const tagSources = tagSourceNames(currentProject, currentProject?.group, projectAccount, projectAccount?.client);
    const defaultDueDate = (() => {
        const inAWeek = toLocalDay(addDays(new Date(), 7));
        return projectDueDate && projectDueDate < inAWeek ? projectDueDate : inAWeek;
    })();
    const buildInitialTaskData = useCallback(() => ({
        name: '',
        overview: '',
        project: projectId,
        priority: 'Medium',
        startDate: toLocalDay(),
        dueDate: defaultDueDate,
        privacy: 'Public',
        billingType: 'Parent',
        skill: projectSkillId,
        department: projectDepartmentId,
        responsible: null,
        assignees: [],
        budget: {
            budgeted: 0,
            quoted: 0,
        },
        rush: false,
        checklistTemplate: ''
    }), [projectId, projectDepartmentId, projectSkillId, defaultDueDate]);

    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [taskData, setTaskData] = useState(buildInitialTaskData);
    const [formKey, setFormKey] = useState(0);

    const [formErrors, setFormErrors] = useState({});
    const [isUserAssignmentOpen, setIsUserAssignmentOpen] = useState(false);
    const [dueDateTouched, setDueDateTouched] = useState(false);
    const [extendedDueDate, setExtendedDueDate] = useState('');
    const [isExtending, setIsExtending] = useState(false);

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value?.trim() === '' ? 'Task name is required' : '';
            case 'overview':
                return value?.trim() === '' ? 'Task overview is required' : '';
            case 'skill':
                return value === '' ? 'Skill is required' : '';
            case 'startDate':
                return !value ? 'Start date is required' : '';
            case 'dueDate':
                if (!value) return 'Due date is required';
                return projectDueDate && value > projectDueDate ? `Cannot be after the project's due date, ${projectDueDate}` : '';
            default:
                return '';
        }
    };

    const validateAllFields = () => {
        const errors = {};
        let isValid = true;

        // Validate required fields
        const requiredFields = ['name', 'overview', 'skill', 'startDate', 'dueDate'];
        
        requiredFields.forEach(field => {
            const value = taskData[field];
            const error = validateField(field, value);
            if (error) {
                errors[field] = error;
                isValid = false;
            }
        });

        setFormErrors(errors);
        return isValid;
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name === 'dueDate') setDueDateTouched(true);
        if (name === 'budgeted' || name === 'quoted') {
            setTaskData(prevData => ({
                ...prevData,
                budget: {
                    ...prevData.budget,
                    [name]: parseFloat(value) || 0
                }
            }));
        } else {
            setTaskData(prevData => ({ ...prevData, [name]: value }));
        }

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleOverviewChange = (content) => {
        setTaskData(prevData => ({ ...prevData, overview: content }));
        
        // Clear error when user starts typing
        if (formErrors.overview) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.overview;
                return newErrors;
            });
        }
    };

    const handleBlur = useCallback((event) => {
        const { name } = event.target;
        const value = event.target.value !== undefined ? event.target.value : taskData[name];
        const error = validateField(name, value);
        if (error) {
            setFormErrors(prev => ({
                ...prev,
                [name]: error,
            }));
        } else {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [taskData]);

    const handleSubmit = () => {
        if (isLocked) return;
        if (isOverageBlocked) return;
        if (!validateAllFields()) {
            return;
        }

        const apiData = {
            ...taskData,
            department: taskData.department || null,
            checklistTemplate: taskData.checklistTemplate || null,
            projectId,
            account: accountId,
            attachments: restoreAttachments(attachments),
            ...tagDraft.assignments,
        };
        setIsCreating(true);
        dispatch(createTask(apiData, (response) => {
            if (!response) {
                setIsCreating(false);
            } else if (response.task) {
                dispatch(triggerRefresh({ triggerName: 'project' }));
                setIsCreating(false);
                onClose();
            } else {
                setIsCreating(false);
                dispatch(showAlert({ severity: 'error', message: 'Failed to create task' }));
            }
        }));
    };

    const handleOpenUserAssignment = () => {
        setIsUserAssignmentOpen(true);
    };

    const handleCloseUserAssignment = () => {
        setIsUserAssignmentOpen(false);
    };

    const handleAssign = (taskId, assignees, responsible) => {
        setTaskData(prevData => ({
            ...prevData,
            responsible: responsible,
            assignees: assignees.filter(user => user._id !== responsible._id)
        }));
    };

    useEffect(() => {
        if (!open || projectIsOverdue || dueDateTouched) return;
        const responsibleId = taskData.responsible?._id;
        if (!responsibleId || !projectId) return;

        let current = true;
        const timer = setTimeout(() => {
            dispatch(getSuggestedDueDate(projectId, responsibleId, taskData.budget?.budgeted || 0, (data) => {
                if (!current || !data?.suggestedDate) return;
                setTaskData((prev) => ({ ...prev, dueDate: data.suggestedDate }));
            }));
        }, 400);
        return () => { current = false; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId, projectIsOverdue, dueDateTouched, taskData.responsible?._id, taskData.budget?.budgeted, dispatch]);

    const handleExtendProject = () => {
        if (!extendedDueDate) return;
        setIsExtending(true);
        dispatch(updateProject({
            projectId,
            projectData: { dueDate: extendedDueDate },
            callback: (data) => {
                setIsExtending(false);
                if (data) setExtendedDueDate('');
            },
        }));
    };

    const isDueDateWithinTwoDays = () => {
        if (!taskData.dueDate) return false;
        return differenceInDays(taskData.dueDate, toLocalDay()) <= 2;
    };

    useEffect(() => {
        isDueDateWithinTwoDays() ? setTaskData(prevData => ({ ...prevData, rush: true })) : setTaskData(prevData => ({ ...prevData, rush: false }));
    }, [taskData.dueDate]);

    useEffect(() => {
        if (open) {
            dispatch(getDepartments(0, 100, (result) => setDepartments(result || [])));
            if (canViewTemplates) {
                dispatch(getTemplates('checklist', 0, 200, (result) => setChecklistTemplates((result || []).filter((t) => t.templateType === 'checklist'))));
            }
        }
    }, [open, canViewTemplates, dispatch]);

    useEffect(() => {
        if (open) {
            setOverageOverride(false);
            setTaskData(buildInitialTaskData());
            setDueDateTouched(false);
            setAttachments([]);
            setFormErrors({});
            setIsCreating(false);
            setFormKey((k) => k + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create Task</DialogTitle>
            <StyledDialogContent>
                <RetainerOverageNotice status={retainerStatus} requireOverride override={overageOverride} onOverrideChange={setOverageOverride} />
                <FormField>
                    <TextField
                        fullWidth
                        label="Task Name"
                        name="name"
                        value={taskData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={Boolean(formErrors.name)}
                        helperText={formErrors.name || ""}
                        required
                    />
                </FormField>

                <FormField>
                    <MarkdownEditor key={`overview-${formKey}`} initialContent="" onChange={handleOverviewChange} placeholder="Task overview..." />
                </FormField>

                <FormField>
                    <FilePondUploader key={`upload-${formKey}`} setAttachments={setAttachments} onUploadStatusChange={setIsUploading} />
                </FormField>

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select name="priority" value={taskData.priority} onChange={handleInputChange} label="Priority">
                            <MenuItem value="Lowest">Lowest</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Highest">Highest</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    <TagPicker draft={tagDraft} parentType="Project" parentId={projectId} clientId={tagClientId} clientName={projectAccount?.client?.name} accountId={projectAccount} sourceNames={tagSources} />
                </FormField>

                <Row>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Start Date"
                            name="startDate"
                            value={taskData.startDate}
                            onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                            error={Boolean(formErrors.startDate)}
                            helperText={formErrors.startDate || ""}
                            required
                            minDate={minStartDate}
                            slotProps={{ textField: { onBlur: () => handleBlur({ target: { name: "startDate" } }) } }}
                        />
                    </HalfWidthField>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Due Date"
                            name="dueDate"
                            value={taskData.dueDate}
                            onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                            error={Boolean(formErrors.dueDate)}
                            helperText={formErrors.dueDate || ""}
                            required
                            disabled={projectIsOverdue}
                            minDate={toLocalDay(addDays(new Date(`${minStartDate}T00:00:00`), 1))}
                            maxDate={projectDueDate || undefined}
                            slotProps={{ textField: { onBlur: () => handleBlur({ target: { name: "dueDate" } }) } }}
                        />
                    </HalfWidthField>
                </Row>

                {projectIsOverdue && (
                    <FormField>
                        <Typography variant="body2" color="text.secondary">
                            {`This project was due ${projectDueDate}.`}
                        </Typography>
                        {canEditProjects ? (
                            <ExtendProjectRow>
                                <AppDatePicker
                                    label="Extend project to"
                                    name="extendedDueDate"
                                    value={extendedDueDate}
                                    onChange={(v) => setExtendedDueDate(v)}
                                    minDate={toLocalDay()}
                                    fullWidth={false}
                                    size="small"
                                />
                                <Button variant="outlined" onClick={handleExtendProject} disabled={!extendedDueDate || isExtending}>
                                    {isExtending ? 'Extending...' : 'Extend project'}
                                </Button>
                            </ExtendProjectRow>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Ask a project manager to extend it.
                            </Typography>
                        )}
                    </FormField>
                )}

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Privacy</InputLabel>
                        <Select name="privacy" value="Public" onChange={handleInputChange} label="Privacy">
                            <MenuItem value="Public">Public</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Billing Type</InputLabel>
                        <Select name="billingType" value={taskData.billingType} onChange={handleInputChange} label="Billing Type">
                            <MenuItem value="Parent">Parent</MenuItem>
                            <MenuItem value="Retainer">Retainer</MenuItem>
                            <MenuItem value="Hourly">Hourly</MenuItem>
                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                            <MenuItem value="Internal">Internal</MenuItem>
                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    <FormControl fullWidth error={Boolean(formErrors.skill)}>
                        <InputLabel>Skill</InputLabel>
                        <Select name="skill" value={taskData.skill} onChange={handleInputChange} onBlur={handleBlur} label="Skill" required>
                            {selectableSkills(skills, taskData.skill).map((skill) => (
                                <MenuItem key={skill._id} value={skill._id}>
                                    {skillOptionLabel(skill)}
                                </MenuItem>
                            ))}
                        </Select>
                        {formErrors.skill && (
                            <Typography variant="caption" color="error">
                                {formErrors.skill}
                            </Typography>
                        )}
                    </FormControl>
                </FormField>

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Department</InputLabel>
                        <Select name="department" value={taskData.department} onChange={handleInputChange} label="Department">
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {departments.map((department) => (
                                <MenuItem key={department._id} value={department._id}>
                                    {department.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FormField>

                {checklistTemplates.length > 0 && (
                    <FormField>
                        <FormControl fullWidth>
                            <InputLabel>Checklist Template</InputLabel>
                            <Select name="checklistTemplate" value={taskData.checklistTemplate} onChange={handleInputChange} label="Checklist Template">
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {checklistTemplates.map((template) => (
                                    <MenuItem key={template._id} value={template._id}>
                                        {template.name} ({template.items?.length || 0} steps)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FieldHelper variant="caption">
                            Optional. The chosen template's steps are copied onto the new task's checklist, where you can edit or remove them later.
                        </FieldHelper>
                    </FormField>
                )}

                <FormField>
                    <Typography variant="subtitle1">Responsible & Assignees</Typography>
                    <UserChipsContainer>
                        {taskData.responsible && (
                            <Chip label={`${taskData.responsible.fullName} (Responsible)`} onDelete={() => setTaskData((prev) => ({ ...prev, responsible: null }))} />
                        )}
                        {taskData.assignees.map((assignee) => (
                            <Chip
                                key={assignee._id}
                                label={assignee.fullName}
                                onDelete={() =>
                                    setTaskData((prev) => ({
                                        ...prev,
                                        assignees: prev.assignees.filter((a) => a._id !== assignee._id),
                                    }))
                                }
                            />
                        ))}
                    </UserChipsContainer>
                    <Button onClick={handleOpenUserAssignment} variant="outlined">
                        Assign Users
                    </Button>
                </FormField>

                <Row>
                    <HalfWidthField>
                        <TextField fullWidth label="Budgeted" name="budgeted" type="number" onChange={handleInputChange} />
                    </HalfWidthField>
                    <HalfWidthField>
                        <TextField fullWidth label="Quoted" name="quoted" type="number" onChange={handleInputChange} />
                    </HalfWidthField>
                </Row>

                <FormField>
                    <FormControlLabel
                        control={
                            <Switch
                                onChange={(e) =>
                                    setTaskData((prev) => ({
                                        ...prev,
                                        rush: e.target.checked,
                                    }))
                                }
                                name="rush"
                                checked={taskData.rush}
                                disabled={isDueDateWithinTwoDays()}
                            />
                        }
                        label="Rush Job"
                    />
                </FormField>
            </StyledDialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isUploading || isCreating || isLocked || isOverageBlocked || projectIsOverdue || tagDraft.loading}>
                    {isCreating ? (
                        <>
                            Creating Task... <CircularProgress size={20} />
                        </>
                    ) : (
                        "Create Task"
                    )}
                </Button>
            </DialogActions>

            <UserAssignmentDialog
                isOpen={isUserAssignmentOpen}
                onClose={handleCloseUserAssignment}
                objectId={projectId}
                objectType="task"
                onAssign={handleAssign}
                isNewObject={true}
                accountId={accountId}
            />
        </StyledDialog>
    );
};

export default CreateTaskDialog;